<script lang="ts" setup>
import { i18n } from '@/i18n';
import { SelectYearMonthPattern } from '../../interfaces/UploadFile';
import SelectableField from '../SelectableField.vue';
import {
  AlterableHeaderInfo,
  isRequireAlterableHeader,
  SelectYearMonthPatternInHeader,
  SelectYearMonthPatternInlined,
} from '../../interfaces/LinkingInput';
import InputExcelYearMonthInfo from './InputExcelYearMonthInfo.vue';
import { RegisterInputViewModel } from '../../RegisterInputViewModel';
import { computed } from 'vue';

const t = i18n.t;
const vm = new RegisterInputViewModel();

/**
 * 呼び出し元
 */
export type From = 'LinkingInput' | 'HomeInput' | 'FinishInput';

const props = defineProps<{
  from: From;
  selectedFieldId: string;
  suggestions: string[];
  useFormatType: SelectYearMonthPattern;
  alterableHeaderInfos: AlterableHeaderInfo[];
  inlinedYearMonthInfo: SelectYearMonthPatternInlined;
  inHeaderYearMonthInfo: SelectYearMonthPatternInHeader;
}>();

const emits = defineEmits<{
  (e: 'update-selected-field', header: AlterableHeaderInfo, updateValue: string): void;
  (e: 'update-selected-field-id', selectedFieldId: string): void;
  (e: 'update-inline-header-name', updateValue: string): void;
  (e: 'update-in-header-start-column-number', updateValue: string): void;
  (e: 'update-in-header-end-column-number', updateValue: string): void;
  (e: 'update-in-header-start', updateValue: string): void;
  (e: 'update-in-header-end', updateValue: string): void;
}>();

/**
 * 選択されたフィールドを更新する
 * @param header - フィールド
 * @param updateValue - 更新する値
 */
const updateSelectedField = (header: AlterableHeaderInfo, updateValue: string) => {
  emits('update-selected-field', header, updateValue);
};

/**
 * 選択されたフィールドIDを更新する
 * @param id - フィールドID
 */
const updateSelectedFieldId = (id: string) => {
  emits('update-selected-field-id', id);
};

/**
 * getter
 * @param header - ヘッダー
 */
const unusedLabel = (header: AlterableHeaderInfo) => {
  if (header.header === 'Package' && header.unused) {
    return header.unused ? t('取引紐付け：文言不使用（数値）') : '';
  } else if (header.unused) {
    return header.unused ? t('取引紐付け：文言不使用') : '';
  } else {
    return '';
  }
};

const isEditing = computed(() => props.from === 'LinkingInput');
</script>

<template>
  <div class="linking-input__linking-container linking-container">
    <div class="linking-container__left">
      <template v-for="header in alterableHeaderInfos" :key="`linking-input-header-${header.value}`">
        <div class="linking-container__at">
          <div class="linking-container__at--content">
            <div class="linking-container__at--label label-container">
              <span class="linking-container__at--label">{{ vm.displayHeaderTitle(header.header) }}</span>
              <template v-if="header.unused != null && isEditing && isRequireAlterableHeader(header.header)">
                <input v-model="header.unused" type="checkbox" class="label-container__checkbox" />
                <span class="label-container__unused-label">{{ t('取引紐付け：チェックボックス：不使用') }}</span>
              </template>
            </div>
            <template v-if="!header.unused">
              <SelectableField
                v-if="isEditing"
                :value="header.value"
                :selected-id="selectedFieldId"
                :hint="t('取引紐付け：サジェストヒント')"
                :suggestions="suggestions"
                @update-value="updateSelectedField(header, $event)"
                @update-selected-id="updateSelectedFieldId"
              />
              <span v-else class="field--non-editable">
                {{ header.value || (isRequireAlterableHeader(header.header) ? '-' : '') }}
              </span>
            </template>
            <template v-else>
              <span class="linking-container__at--unused">{{ unusedLabel(header) }}</span>
            </template>
          </div>
          <span v-if="isEditing || header.validateError" class="linking-container__at--error">{{
            header.validateError ? t('取引紐付け：アラート文言：必須項目') : '　'
          }}</span>
        </div>
      </template>
    </div>

    <div class="linking-container__separator" />

    <div class="linking-container__right">
      <InputExcelYearMonthInfo
        :select-year-month-pattern="useFormatType"
        :inlined-year-month-info="inlinedYearMonthInfo"
        :in-header-year-month-info="inHeaderYearMonthInfo"
        @update-inline-header-name="($event) => emits('update-inline-header-name', $event)"
        @update-in-header-start-column-number="($event) => emits('update-in-header-start-column-number', $event)"
        @update-in-header-end-column-number="($event) => emits('update-in-header-end-column-number', $event)"
        @update-in-header-start="($event) => emits('update-in-header-start', $event)"
        @update-in-header-end="($event) => emits('update-in-header-end', $event)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.linking-container {
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto auto;
  align-items: start;
  min-height: 0;

  &__left {
    display: grid;
    gap: 32px;
    grid-template-columns: repeat(auto-fit, minmax(100px, 180px));
    grid-auto-rows: max-content;
    align-content: start;
  }

  &__at {
    display: grid;
    grid-template-rows: repeat(2, fit-content(100%));

    &--content {
      display: grid;
      grid-template-rows: repeat(2, fit-content(100%));
      border-bottom: 1px solid $borderDefault;
    }

    &--label {
      margin-bottom: 8px;
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $textDefault;
    }

    &--error {
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $semanticError;
    }

    &--unused {
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $semanticWarning;
    }
  }

  &__separator {
    background-color: $borderDefault;
    width: 100%;
    height: 1px;
  }

  &__right {
    width: 100%;
    min-height: 0;
  }
}

.label-container {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  background-color: $semanticWhite;

  &__title {
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    font-weight: 400;
    color: $textDefault;
  }

  &__checkbox {
    height: 16px;
    width: 16px;
  }

  &__unused-label {
    margin-left: 4px;
    padding-bottom: 4px;
    font-family: Noto Sans JP;
    font-size: $fontSizeSS;
    font-weight: 400;
    color: $textDefault;
  }
}

.field {
  &--non-editable {
    display: block;
    min-height: 32px;
    padding: 5px 0;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 14px;
  }
}
</style>
